import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  Checkbox,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';

const formatList = (items) => {
  if (!items?.length) return 'None';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const HighlightedText = ({ children }) => (
  <span style={{ color: 'red', fontWeight: 'bold' }}>{children}</span>
);

const Step3 = ({
  formik: { values },
  tenantInsuranceConsent,
  setTenantInsuranceConsent,
}) => {
  const appliancesString = formatList(values?.property_details?.appliances || []);

  return (
    <Box className="lease-form-step3">
      {/* Section 8: Appliances */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">8. APPLIANCES</Typography>
        <Typography variant="body2" className="section-content">
          The Tenant agrees to keep clean and in good condition all assets, including appliances,
          furnishings, and equipment contained at the time of possession in the Premises, which
          specifically includes, without limitation:<HighlightedText>{appliancesString}</HighlightedText>.
        </Typography>
      </Box>

      {/* Section 9 – Other Occupants */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title"> 9. OTHER OCCUPANTS</Typography>
        <Typography variant="body2" className="section-content">
          The tenant agrees that in addition to the tenant, the premises may be occupied only by the following persons:
          <br />
          <OccupantsForm values={values} />
          Together with any natural increase in the tenant’s family, but not to exceed a total of 2 Adult unless the landlord consents in
          writing to the occupation of the premises by some other or additional person or persons. Each approved additional individual
          occupant will be charged $50.00 extra per month.
        </Typography>
      </Box>

      {/* Section 10 – Condition Report */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">10. CONDITION REPORT</Typography>
        <Typography variant="body2" className="section-content">
          Prior to the tenant taking possession of the premises, the landlord and the tenant shall complete and execute an entry condition report detailing the condition of the premises, fixtures and appliances, and the said report shall be conclusive evidence of the condition of the premises, fixtures, and appliances, an exit report shall be similarly completed and executed and all charges for repair of damages and cleaning of the premises will be based on these reports. When Tenant is moving out- they are responsible to get all carpets steam cleaned and provide Management with a proof of receipt.
        </Typography>
      </Box>

      {/* Section 11 */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">
          11. TENANT INSURANCE
        </Typography>

        <Typography
          variant="body2"
          className="section-content"
          sx={{
            backgroundColor: '#ffff99',
            color: 'black',
            p: 1,
            borderRadius: 1,
            fontWeight: 'bold'
          }}
        >
          The Tenant promises to hold a valid insurance policy, which includes the Tenant’s contents in the Home,
          including contents in vehicle. The policy is required to insure the Tenants and any guests against loss or damages,
          including personal injury and death. The Tenant promises to carry general comprehensive liability insurance to a
          minimum of $2 Million Liability coverage. Proof of current and accurate Tenant’s Insurance is required before
          move-in. Tenant Insurance is mandatory.
        </Typography>
        <Box sx={{ mt: 2, pl: 2, pr: 2, pb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={tenantInsuranceConsent}
                onChange={(e) => setTenantInsuranceConsent(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" sx={{ color: '#444' }}>
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  I acknowledge and agree
                </Box>{' '}that I will obtain and continuously maintain tenant insurance
                with at least $2,000,000 in general comprehensive liability coverage as required above.
                I understand that proof of insurance must be provided before move-in and that failure to
                maintain valid insurance may result in eviction.
              </Typography>
            }
            sx={{ alignItems: 'flex-start', mb: 1 }}
          />
          {!tenantInsuranceConsent && (
            <FormHelperText error sx={{ ml: '32px', mt: -0.5 }}>
              Required – please confirm your agreement
            </FormHelperText>
          )}
        </Box>
      </Box>

      {/* Section 12 – SAFETY & REPAIRS */}
      <Box className="section">
        THE TENANT HEREBY COVENANTS WITH THE LANDLORD AS FOLLOWS:
        <br />
        <br />
        <Typography variant="subtitle2" className="section-title">12. SAFETY & REPAIRS</Typography>
        <Typography variant="body2" className="section-content">
          The tenant will not do anything, nor neglect to do anything by which fire or health hazard is created. The tenant will comply with all fire, health, and sanitation regulations. The tenant shall take good care of the premises and keep the same in reasonably clean condition. The tenant will be responsible for repairing and replacing windows, screens, light bulbs, fuses, fixtures, appliances and all floor coverings and counter tops damaged, broken, removed, destroyed, stained, or soiled by the tenant, or the tenant’s guests or invitees. The tenant will be responsible for and promptly pay on demand all charges relating to any service supplied to or used on the premises where properly chargeable to the tenant through causation or neglect including maintenance of yards and sidewalks, plugged toilets, sinks and drains etc. The cost of any damage resulting directly or indirectly there from. Charges will be levied at $40 per hour plus travel time and equipment use if done by the landlord’s own forces, or at the charge levied by people he may hire plus 20% to cover overseeing. The tenant shall be responsible for all damages resulting from the escape of water within or from the leased premises, and from the use of defective electrical or other appliances of any kind. Leaks in pipes, are to be repaired by the landlord within a reasonable time after notice, but the landlord shall not be liable or responsible for any damages resulting from such leaks or overflow.
        </Typography>
      </Box>

      {/* Section 13 */}
      <Box className="section">
        <Typography variant="subtitle2" className="section-title">13. ASSIGNMENT & GUESTS</Typography>
        <Typography variant="body2" className="section-content">
          During the term of this lease, if the tenant wants to sublet, assign or re-let the Leased Premises, the permission
          needs to be obtained from the landlord. (This Includes Parking stalls/Storages). The tenant will not leave guests
          in charge of the leased premises without first obtaining the written consent of the landlord, such consent will be
          arbitrarily withheld, nor allow guests to reside in the premises for a period greater than 1 week, (7 Days) without
          written consent from the landlord.
        </Typography>
      </Box>
    </Box >
  );
};

export { Step3 }

const OccupantsForm = ({ values }) => {
  const convertAge = (months) => {
    if (!months || isNaN(months)) return '-';
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    return years > 0
      ? `${years} year${years > 1 ? 's' : ''}${remainingMonths > 0 ? ` and ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}` : ''}`
      : `${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
  };

  return (
    <Box className="occupants-details-table" sx={{ my: 2 }}>
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Relationship</TableCell>
              <TableCell>Age</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {values.occupants.length > 0 ? (
              values.occupants.map((occupant, index) => (
                <TableRow key={index}>
                  <TableCell>{occupant.name || '-'}</TableCell>
                  <TableCell>{occupant.relationship || '-'}</TableCell>
                  <TableCell>{convertAge(occupant.age)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ color: '#aaa' }}>
                  No occupants applicable.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};